import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { getData } from '../../../api/indexedDBHandler'
import PairTournamentProtocolPDF from './PairTournamentProtocolPDF'
import { getLogoUrl } from '../../../api/Logo'
import { dateNowProtocol } from '../../../api/formatDate'

// Выносим логику генерации протокола в отдельную функцию
const generateProtocolData = async (id) => {
  const resultsU = await getData('teams', 'id', id)
  if (resultsU.length === 0) throw new Error('No team data found')

  const { value: teamValue } = resultsU[0]
  const filteredTeams = Object.values(teamValue).filter(
    (teamEntry) =>
      teamEntry !== null &&
      typeof teamEntry === 'object' &&
      teamEntry.team?.status === 'Заявка одобрена' &&
      teamEntry.team?.status_location === 1
  )

  const resultsS = await getData('stage', 'id', id)
  if (resultsS.length === 0) throw new Error('No stage data found')

  const { value: stageValue } = resultsS[0]
  const stageArray = Object.values(stageValue).filter(
    (teamEntry) => teamEntry !== null && typeof teamEntry === 'object'
  )

  const resultsT = await getData('tournaments', 'id', id)
  if (resultsT.length === 0) throw new Error('No tournament data found')

  const { value: tournamentValue } = resultsT[0]
  const isFinish = tournamentValue.status_cup === 'Турнир завершен'
  const allLogos = Object.values(tournamentValue.sponsor || {})
  const administrator = Object.values(tournamentValue.administrator || [])

  const getname = (teamData) => {
    //  console.log('teamData', teamData)
    if (resultsT[0].competition_type == 'Рейтинговое соревнование') {
      return teamData?.team_name_ru
    }
    return teamData?.preferred_name == 'en'
      ? teamData?.team_name_en
        ? teamData?.team_name_en
        : teamData?.team_name_ru
      : teamData?.team_name_ru
  }

  // Обработка данных пар (как в оригинальном коде)
  const pairs = filteredTeams.map((team) => {
    const sp1 = team.athlete[0]?.fio || ''
    const sp1Rank = team.athlete[0]?.sports_category
      ? ` (${team.athlete[0]?.sports_category})`
      : ''
    const sp2 = team.athlete[1]?.fio ? `, ${team.athlete[1]?.fio}` : ''
    const sp2Rank = team.athlete[1]?.sports_category
      ? ` (${team.athlete[1]?.sports_category})`
      : ''
    const tname = getname(team.team)
    return {
      id: team.team.id,
      zona: team.team.zona,
      teamName: tname,
      sp: `${sp1}${sp1Rank}${sp2}${sp2Rank}`,
      sector: team.team.sector,
      totalWeight: 0,
      fishCount: 0,
      biggestFish: 0,
      averageWeight: 0,
      weightDifference: 0,
      disqualified: team.team.status === 'Снята',
    }
  })

  // Подсчет уловов (как в оригинальном коде)
  stageArray[0]?.forEach((entry) => {
    const pair = pairs.find((p) => p.id === entry.teamId)
    const validFishList = entry.fishList.filter((fish) => fish.weight > 0)
    if (
      pair &&
      entry.fishList &&
      entry.fishList.length > 0 &&
      validFishList.length > 0
    ) {
      const weight = entry.fishList.reduce((sum, fish) => sum + fish.weight, 0)
      const biggestFish = Math.max(...entry.fishList.map((fish) => fish.weight))

      pair.totalWeight += weight
      pair.fishCount += entry.fishList.length
      pair.biggestFish = Math.max(pair.biggestFish, biggestFish)
    }
  })

  // Вычисляем средний вес рыбы
  pairs.forEach((pair) => {
    if (pair.fishCount > 0) {
      pair.averageWeight = Math.round(pair.totalWeight / pair.fishCount)
    }
  })

  // Разделяем пары: с рыбой, без рыбы, снятые
  const withFish = pairs.filter((p) => p.totalWeight > 0 && !p.disqualified)
  const withoutFish = pairs.filter(
    (p) => p.totalWeight === 0 && !p.disqualified
  )
  const disqualified = pairs.filter((p) => p.disqualified)

  // Сортировка пар с рыбой по правилам:
  // 1. Вес (убывание), 2. Средний вес (возрастание), 3. Крупная рыба (убывание)
  withFish.sort((a, b) => {
    if (b.totalWeight !== a.totalWeight) return b.totalWeight - a.totalWeight

    const avgWeightA = a.fishCount > 0 ? a.totalWeight / a.fishCount : 0
    const avgWeightB = b.fishCount > 0 ? b.totalWeight / b.fishCount : 0
    if (avgWeightA !== avgWeightB) return avgWeightA - avgWeightB

    return b.biggestFish - a.biggestFish
  })

  // Назначение мест с учетом ничьих
  let currentRank = 1
  for (let i = 0; i < withFish.length; i++) {
    if (i > 0) {
      const prev = withFish[i - 1]
      const curr = withFish[i]
      const isTie =
        prev.totalWeight === curr.totalWeight &&
        prev.totalWeight / prev.fishCount ===
          curr.totalWeight / curr.fishCount &&
        prev.biggestFish === curr.biggestFish
      if (!isTie) currentRank = i + 1
    }
    withFish[i].overallRank = currentRank
  }

  // Пары без рыбы получают последнее место
  const lastRank = pairs.length
  withoutFish.forEach((pair) => {
    pair.overallRank = lastRank
  })

  // Снятые пары получают место = totalPairs + 3
  disqualified.forEach((pair) => {
    pair.overallRank = lastRank + 3
  })

  // Объединяем все пары
  const allPairsSorted = [...withFish, ...withoutFish, ...disqualified]

  // Определяем пару-лидера
  const leaderWeight =
    allPairsSorted.length > 0 ? allPairsSorted[0].totalWeight : 0

  // Вычисляем отставание от лидера
  allPairsSorted.forEach((pair) => {
    pair.weightDifference = leaderWeight - pair.totalWeight
  })

  // Поиск самой крупной рыбы
  const biggestFish = allPairsSorted.reduce(
    (max, pair) =>
      pair.biggestFish > max.weight
        ? { weight: pair.biggestFish, pair: pair.sp }
        : max,
    { weight: 0, pair: '' }
  )

  // Общие данные по соревнованию
  const totalFishCount = allPairsSorted.reduce(
    (sum, pair) => sum + pair.fishCount,
    0
  )
  const totalWeight = allPairsSorted.reduce(
    (sum, pair) => sum + pair.totalWeight,
    0
  )
  const averageFishWeight =
    totalFishCount > 0 ? Math.round(totalWeight / totalFishCount) : 0
  // Остальная логика обработки данных...
  // (добавьте вашу логику обработки данных здесь)

  return {
    allLogos,
    mainLogo: getLogoUrl(tournamentValue?.logocup),
    data: allPairsSorted,
    administrator,
    tournament: tournamentValue,
    date: dateNowProtocol(new Date()),
    totalFishCount: totalFishCount,
    totalWeight: totalWeight,
    averageFishWeight: averageFishWeight,

    biggestFish: biggestFish,
    isFinish: isFinish,
    // isFinish,
    // processedData: [], // Замените на ваши обработанные данные
    // totalFishCount: 0, // Замените на реальные значения
    // totalWeight: 0,
    // averageFishWeight: 0,
    // biggestFish: { weight: 0, pair: '' },
  }
}

const DocProtocolPair = ({ isProtocolSvodCallback }) => {}
export default {
  generate: async function (id, finish = false) {
    try {
      const protocolData = await generateProtocolData(id)
      console.log('protocolData', protocolData)
      const pdfDoc = (
        <PairTournamentProtocolPDF
          allLogo={protocolData.allLogos}
          mainLogo={getLogoUrl(protocolData.tournament?.logocup)}
          data={protocolData.data}
          administrator={protocolData.administrator}
          tournament={protocolData.tournament}
          date={dateNowProtocol(new Date())}
          totalFishCount={protocolData.totalFishCount}
          totalWeight={protocolData.totalWeight}
          averageFishWeight={protocolData.averageFishWeight}
          biggestFish={protocolData.biggestFish}
          isfinish={finish}
        />
      )

      const blob = await pdf(pdfDoc).toBlob()
      const url = URL.createObjectURL(blob)

      //   const a = document.createElement('a')
      //   a.href = url
      //   a.download = `парный_протокол_${dateNowProtocol(new Date())}.pdf`
      //   document.body.appendChild(a)
      //   a.click()
      //   document.body.removeChild(a)
      const servaurl = await uploadPdfToServer(blob, id)
      return {
        blob,
        previewUrl: servaurl,
        data: protocolData,
      }
    } catch (error) {
      console.error('Ошибка при генерации протокола:', error)
      throw error
    }
  },
}
const uploadPdfToServer = async (blob, id) => {
  try {
    const formData = new FormData()
    formData.append(
      'pdf',
      blob,
      `парный_протокол_${dateNowProtocol(new Date())}.pdf`
    )
    formData.append('id', id)
    formData.append('isPost', 3)

    formData.append('type', 'ProtocolSvod')
    const response = await fetch(
      'https://www.admin-protocarp.ru/php/uploads/upload.php',
      {
        method: 'POST',
        body: formData,
      }
    )

    if (response.ok) {
      const result = await response.json()
      // Обновляем URL на серверный, если нужно
      return { fileUrl: result.fileUrl }
    } else {
      console.error('Ошибка загрузки файла на сервер')
      return { fileUrl: null }
    }
  } catch (error) {
    console.error('Ошибка при загрузке на сервер:', error)
    return { fileUrl: null }
  }
}
